import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import {
    ImprovedResponseData,
    ImprovedCandidateResponse as DetailTabImprovedResponse,
} from "src/pages/ReportPage/Tabs/DetailsTab/types";

// This interface is kept for backward compatibility
export interface ImprovedResponse {
    title: string;
    content: string;
}

// Export the real type used in the DetailsTab
export type { ImprovedResponseData };

interface GetImprovedResponseParams {
    sessionId: string;
}

interface GetImprovedResponseResponse {
    found: boolean;
    data: ImprovedResponseData[];
}

const getImprovedCandidateResponse = httpsCallable<
    GetImprovedResponseParams,
    GetImprovedResponseResponse
>(functions, "getImprovedCandidateResponse");

export { getImprovedCandidateResponse };

const fetchImprovedResponses = async (
    sessionId?: string
): Promise<ImprovedResponseData[] | null> => {
    if (!sessionId) {
        console.log("Missing sessionId, returning null");
        return null;
    }

    try {
        console.log(`Fetching improved responses for session ${sessionId}`);
        const response = await getImprovedCandidateResponse({
            sessionId,
        });

        console.log("Improved responses data:", response.data);
        const { found, data } = response.data;

        if (!found) {
            console.log("No improved responses found");
            return null;
        }

        return data;
    } catch (error) {
        console.error("Error fetching improved responses:", error);
        return null;
    }
};

export function useFetchImprovedResponses(sessionId?: string) {
    const query = useQuery<ImprovedResponseData[] | null>({
        queryKey: ["improvedResponses", sessionId],
        queryFn: () => fetchImprovedResponses(sessionId),
        enabled: !!sessionId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    return {
        improvedResponsesData: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}
